import {useCallback, useMemo, useState} from 'react';

const DEFAULT_PAGE_SIZE = 10;

/**
 * Custom hook for simple data tables with client-side sorting and pagination.
 * Reduces duplication across read-only PeacockDataTable wrappers.
 */
export const useSimpleDataTable = ({
  data,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  sortComparator,
  noDataTitle,
  noDataDescription,
  wrapperClassName = ''
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState(null);

  const sortedData = useMemo(() => {
    if (!sortConfig?.column) return data;
    if (sortComparator) return sortComparator(data, sortConfig);
    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortConfig.column];
      const bVal = b[sortConfig.column];
      if (sortConfig.direction === 'desc') return bVal - aVal;
      return aVal - bVal;
    });
    return sorted;
  }, [data, sortConfig, sortComparator]);

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, pageIndex, pageSize]);

  const handleSorting = useCallback((sorting) => {
    setSortConfig(sorting);
  }, []);

  const handleRowsPerPageChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
  }, []);

  const paginationConfig = useMemo(
    () => ({
      pageIndex,
      setPageIndex,
      onRowsPerPageChange: handleRowsPerPageChange,
      hidePagination: data.length <= pageSize
    }),
    [pageIndex, setPageIndex, handleRowsPerPageChange, data.length, pageSize]
  );

  const selectionConfig = useMemo(() => ({hideCheckbox: true}), []);

  const sortingConfig = useMemo(
    () => ({handleSorting, initialSortConfig: sortConfig}),
    [handleSorting, sortConfig]
  );

  const filterConfig = useMemo(() => ({hideFilterRow: true}), []);

  const toolbarConfig = useMemo(() => ({hideToolbar: true}), []);

  const displayConfig = useMemo(
    () => ({
      noDataTitle,
      noDataDescription,
      tableSize: 'small',
      wrapperClassName
    }),
    [noDataTitle, noDataDescription, wrapperClassName]
  );

  return {
    paginatedData,
    setPageIndex,
    setPageSize,
    setSortConfig,
    paginationConfig,
    selectionConfig,
    sortingConfig,
    filterConfig,
    toolbarConfig,
    displayConfig,
    pageSize
  };
};
